import React, { useState } from 'react';
import { Box, Grid, Typography, CircularProgress, MenuItem } from '@mui/material';
import { TextField, CustomCheckbox, CustomButton, CustomSelect } from '../../../../../v2/MUI_custom_component';
import FileUploadWrapper from '../../../../../v2/Utils/FileUpload';
import CustomFileUploadInput from './CustomFileUploadInput';
import { getAge, calculateAgesAndFlags } from './../../../../../v2/Utils/AgeCalc';
import ReusableModal from '../../../../../v2/MUI_custom_component/Modal/CustomModal';

const EnhancedFileUploadArea = ({ 
    onFileUpload, 
    onFileDelete,
    isLoading, 
    error, 
    uploadedFile, 
    extractedData, 
    phoneNumber, 
    onPhoneChange, 
    relation,
    onRelationChange,
    relations = [],
    onConfirm, 
    onEditManually,
    uploadTitle = "Upload Beneficiary Aadhar",
    processingText = "Processing Aadhar..."
}) => {
    const [confirm, setConfirm] = useState(false);
    const [phoneValidation, setPhoneValidation] = useState('');
    const [relationValidation, setRelationValidation] = useState('');
    const [consentValidation, setConsentValidation] = useState('');
    const [ageValidation, setAgeValidation] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);

    const handleUpload = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onFileUpload(files[0]);
        }
    };

    const handlePhoneChange = (value) => {
        onPhoneChange(value);
        if (!value.trim()) {
            setPhoneValidation('Phone number is required');
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) {
            setPhoneValidation('Please enter a valid 10-digit phone number');
        } else {
            setPhoneValidation('');
        }
    };

    // Reusable validation function
    const validateForm = () => {
        // Reset all validations
        setPhoneValidation('');
        setRelationValidation('');
        setConsentValidation('');
        setAgeValidation('');

        let isValid = true;

        // Phone number validation
        if (!phoneNumber.trim()) {
            setPhoneValidation('Phone number is required');
            isValid = false;
        } else if (!/^\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
            setPhoneValidation('Please enter a valid 10-digit phone number');
            isValid = false;
        }

        // Age validation for beneficiary
        if (extractedData.dob) {
            const ageResult = calculateAgesAndFlags([extractedData.dob]);
            if (ageResult[0]?.ageFlag) {
                const age = ageResult[0]?.age;
                if (age < 18) {
                    setAgeValidation('Age should be 18+');
                    isValid = false;
                } else if (age > 60) {
                    setAgeValidation('Age must be under 60 years');
                    isValid = false;
                }
            }
        }

        // Relation validation for nominee
        if (uploadTitle.includes('Nominee') && !relation) {
            setRelationValidation('Please select a relation');
            isValid = false;
        }

        // Consent validation
        if (!confirm) {
            setConsentValidation('Please confirm the details to proceed');
            isValid = false;
        }

        return isValid;
    };

    const handleConfirmClick = async () => {
        if (isSubmitting) {
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        await onConfirm();
        setIsSubmitting(false);
        setOpenConfirmModal(false);
        
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
             <Grid container spacing={2} sx={{ my: 0}}>
                {uploadTitle.includes('Nominee') && onRelationChange && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="relation-select"
                            label="Relation*"
                            value={relation || ''}
                            onChange={(e) => onRelationChange(e.target.value)}
                            fullWidth
                            error={!!relationValidation}
                            helperText={relationValidation}
                        >
                            {relations && relations.length > 0 ? (
                                relations.map((relationOption) => (
                                    <MenuItem key={relationOption.value} value={relationOption.value}>
                                        {relationOption.label}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>
                                    No relations available
                                </MenuItem>
                            )}
                        </CustomSelect>
                    </Grid>
                )}
            </Grid>
            {isLoading ? (
                <Box sx={{ 
                    border: '2px dashed #ddd',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography variant="body1" color="primary">
                        {processingText}
                    </Typography>
                </Box>
            ) : !uploadedFile ? (
                <Box>
                    <CustomFileUploadInput
                        title={uploadTitle}
                        acceptedFileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
                        onFileSelect={onFileUpload}
                    />
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box>
                    <FileUploadWrapper
                        handleUpload={handleUpload}
                        title={uploadTitle}
                        acceptedFileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
                        existingFileName={uploadedFile.name}
                        existingFileUrl={URL.createObjectURL(uploadedFile)}
                        showOriginalOnDelete={false}
                        onDelete={() => {
                            if (onFileDelete) {
                                setPhoneValidation('');
                                setRelationValidation('');
                                setConsentValidation('');
                                setAgeValidation('');
                                onFileDelete();
                            }
                        }}
                    />
                    {extractedData && Object.keys(extractedData).length > 0 && (
                        <Box sx={{ p: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.full_name}
                                    </Typography>
                                </Grid>
                                
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Date of Birth
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(extractedData.dob).toLocaleDateString('en-GB', { 
                                            day: 'numeric', 
                                            month: 'long', 
                                            year: 'numeric' 
                                        })}
                                    </Typography>
                                </Grid>
                                {extractedData.dob && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Age
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{
                                                color: calculateAgesAndFlags([extractedData.dob])[0]?.ageFlag ? 'error.main' : 'inherit'
                                            }}
                                        >
                                            {getAge(extractedData.dob)} years
                                            {ageValidation && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                    {ageValidation}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Grid>
                                )}
                               {extractedData.gender && (
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Gender
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.gender}
                                    </Typography>
                                </Grid>
                               )}
                                
                            </Grid>
                        </Box>
                    )}
                </Box>
            )}

            {/* Phone Number Field */}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Phone number*"
                        placeholder="Enter Phone Number"
                        value={phoneNumber}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        error={!!phoneValidation}
                        helperText={phoneValidation}
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                        variant="outlined"
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
                <CustomCheckbox 
                    label="Details provided herein are true and I have obtained all relevant organisational consents for the purposes."
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                />
                {consentValidation && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {consentValidation}
                    </Typography>
                )}
                
            </Box>

            {/* Action Button */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <CustomButton
                    variant="filled"
                    onClick={() => {
                        if (validateForm()) {
                            setOpenConfirmModal(true);
                        }
                    }}
                    disabled={
                        isLoading || 
                        isSubmitting || 
                        (!extractedData || Object.keys(extractedData).length === 0) ||
                        !confirm ||
                        !phoneNumber.trim() ||
                        !/^\d{10}$/.test(phoneNumber.replace(/\D/g, '')) ||
                        (uploadTitle.includes('Nominee') && !relation)
                    }
                    // loading={isSubmitting}
                    label="Save & Continue"
                    fullWidth
                />
            </Box>
            <ReusableModal
                open={openConfirmModal}
                onClose={() => {
                    setOpenConfirmModal(false);
                }}
                title="Confirm Details"
                maxWidth="500px"
                scrollable={true}
            >
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        {uploadedFile && (
                            <Box sx={{ mb: 3 }}>
                                {uploadedFile.type === 'application/pdf' ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '60px',
                                                height: '60px',
                                                backgroundColor: '#dc3545',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                PDF
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', }}>
                                                PDF Document Uploaded
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666'}}>
                                                {uploadedFile.name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    // Image Document Display
                                    <Box
                                        component="img"
                                        src={URL.createObjectURL(uploadedFile)}
                                        alt="Uploaded document"
                                        sx={{
                                            maxWidth: '100%',
                                        }}
                                    />
                                )}
                                
                            </Box>
                        )}

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={6}>
                                <Typography variant="labelD" sx={{ color: '#666' }}>
                                    Name
                                </Typography>
                                <Typography variant="body1">
                                    {extractedData.full_name}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={6} sm={6}>
                                <Typography variant="labelD" sx={{ color: '#666' }}>
                                    Date of Birth
                                </Typography>
                                <Typography variant="body1">
                                    {new Date(extractedData.dob).toLocaleDateString('en-GB', { 
                                        day: 'numeric', 
                                        month: 'long', 
                                        year: 'numeric' 
                                    })}
                                </Typography>
                            </Grid>
                            {extractedData.dob && (
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Age
                                    </Typography>
                                    <Typography 
                                        variant="body1"
                                        sx={{
                                            color: uploadTitle.includes('Beneficiary') && 
                                                    calculateAgesAndFlags([extractedData.dob])[0]?.ageFlag 
                                                    ? 'error.main' : 'inherit'
                                        }}
                                    >
                                        {getAge(extractedData.dob)} years
                                        {ageValidation && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                {ageValidation}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={6} sm={6}>
                                <Typography variant="labelD" sx={{ color: '#666' }}>
                                    Gender
                                </Typography>
                                <Typography variant="body1">
                                    {extractedData.gender}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <Typography variant="labelD" sx={{ color: '#666' }}>
                                    Phone Number
                                </Typography>
                                <Typography variant="body1">
                                    {phoneNumber}
                                </Typography>
                            </Grid>
                            {uploadTitle.includes('Nominee') && relation && (
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Relation
                                    </Typography>
                                    <Typography variant="body1">
                                        {relations.find(r => r.value === relation)?.label || relation}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid> 


                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            <Typography variant="h3">
                                Confirm all details are correct.
                            </Typography>
                            <Typography variant="body1">
                                Are you sure you want to proceed?
                            </Typography>
                        </Box>
                        
                            <Grid container spacing={2} sx={{ mb: 3, width: '100%' }}>
                                <Grid item xs={6} sm={6}>
                                    <CustomButton
                                        variant="outlined"
                                        onClick={() => {
                                            setOpenConfirmModal(false);
                                            if (onEditManually) {
                                                onEditManually();
                                            }
                                        }}
                                        label="No, Fill Manually"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6} sm={6}>
                                    <CustomButton
                                        variant="filled"
                                        onClick={extractedData && Object.keys(extractedData).length > 0 ? handleConfirmClick : () => {}}
                                        disabled={isLoading || isSubmitting || (!extractedData || Object.keys(extractedData).length === 0)}
                                        loading={isSubmitting}
                                        label="Yes"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        
                </Box>
               
            </ReusableModal>
        </Box>
    );
};

export default EnhancedFileUploadArea;